import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import queryString from 'query-string';

import usePlpNavigation from './use-plp-navigation';

import {
  getFitmentStaggeredTireProductSearch,
  getFitmentTireProductSearch,
  getStaggeredProductSearch,
  getStandardProductSearch
} from '../../../shared/graphql/product-list-queries';
import {
  getFitmentStaggeredWheelProductSearch,
  getFitmentWheelProductSearch,
  getPrebuiltPackageSearch,
  getStaggeredTirePackageSearch,
  getStaggeredWheelPackageSearch,
  getTirePackageSearch,
  getTireSizeProductSearch,
  getWheelPackageSearch,
  getWheelSizeProductSearch
} from '../graphql/plp-queries';

import {
  useDoesProductFit,
  useFeatureFlags,
  useGraphQLClient,
  useMyStore,
  useTireWheelPackage,
  useTreadwellPreferences
} from '../../../shared/hooks/hooks';

import { clearLastPlpProduct } from '../../../shared/utils/back-to-results/back-to-results';
import {
  createProductSearchQueryFromFilters,
  facetsToFilters,
  facetsToSelectedFilters,
  getNormalizedSortNames
} from '../../../shared/utils/filters/filters';
import {
  getVehicleInput,
  parseTireSize
} from '../../../shared/utils/fitment/fitment';
import {
  getNormalizedProductType,
  getSorts,
  isCanonicalProduct
} from '../../../shared/utils/product/product';
import { getTreadwellGQLInput } from '../../../shared/utils/treadwell/treadwell';
import { isArrayWithLength } from '../../../shared/utils/validators/validators';

import { TireSearchOption } from '../../../shared/types/graphql-types';
import { PLPInfoMessageIds } from '../types/plp-products';
import { FEATURE_FLAGS } from '../../../shared/constants/feature-flags';
import { DONT_FIT, EXCLUDED, FIT } from '../../../shared/constants/fit';
import { AXLE_TYPES } from '../../../shared/constants/fitment';
import {
  ACCESSSORY_FILTER_TYPES,
  PRODUCT_TYPES,
  QUERY_TYPES,
  TREADWELL_PRODUCTS_DISPLAY_TYPES
} from '../../../shared/constants/products';
import { SORT_PARAMS } from '../../../shared/constants/search-types';
import { SetSearchTypes } from '../../../shared/constants/set-products';
import { STAGGERED_SEARCH_TYPES } from '../../../shared/constants/staggered-products';

export default function usePlpProducts({
  baseSortValue = undefined,
  defaultInitialFilters = [],
  isAccessorySearch = false,
  isFitmentLoading = false,
  isFitmentSearch = false,
  isPackageSearch = false,
  isPrebuiltPackageSearch = false,
  isSizeSearch = false,
  isStaggeredSearch = false,
  productType,
  queryType,
  selectedFitment
}) {
  const { myStore, nearbyStorePreferences } = useMyStore();

  const { searchPosition, staggeredSearchAxle } = useParams();
  const client = useGraphQLClient();
  const { disableTreadwellFlow, isTreadwellFlow, treadwellPreferences } =
    useTreadwellPreferences();
  const [treadwellProductsDisplayType, setTreadwellProductsDisplayType] =
    useState(TREADWELL_PRODUCTS_DISPLAY_TYPES.DEFAULT);
  const { featureFlags } = useFeatureFlags();
  const defaultSort =
    Boolean(featureFlags?.[FEATURE_FLAGS.__FF_BEST_AVAILABLE_SORT__]) &&
    productType === PRODUCT_TYPES.TIRES
      ? 'bestAvailable-asc'
      : 'bestSeller-asc';
  baseSortValue = baseSortValue || defaultSort;

  const productQueryRef = useRef();
  const { hash, pathname, search } = useLocation();
  const { q, sort: initialSort } = queryString.parse(search);
  const isInitialTreadwellFlowRef = useRef(isTreadwellFlow);
  const hasOEProductInTreadwellRef = useRef(false);

  const {
    breadcrumbs,
    category,
    clearSelectedFilters,
    filters,
    getPagenationUrl,
    initialFacets,
    initialFilters: initFilters,
    metaData,
    queryParameters,
    selectFilters,
    selectedFilters,
    setFilters,
    setPageNum,
    setSelectedFilters,
    setSort,
    subtitle,
    tireSizes,
    updateQueryParams,
    wheelSizes
  } = usePlpNavigation(productType, queryType, selectedFitment, baseSortValue);
  const initialFilters = isArrayWithLength(defaultInitialFilters)
    ? defaultInitialFilters
    : initFilters;

  const isStaggeredFitment =
    selectedFitment?.isStaggered ||
    (selectedFitment?.isDually && productType === PRODUCT_TYPES.WHEELS);
  const staggeredSearchType = useMemo(() => {
    let type;

    if (
      staggeredSearchAxle &&
      STAGGERED_SEARCH_TYPES[staggeredSearchAxle.toUpperCase()]
    ) {
      type = STAGGERED_SEARCH_TYPES[staggeredSearchAxle.toUpperCase()];
    } else if (isStaggeredFitment || isStaggeredSearch) {
      type = STAGGERED_SEARCH_TYPES.SET;
    }

    return type;
  }, [isStaggeredFitment, isStaggeredSearch, staggeredSearchAxle]);

  const { incompletePackage, isPackageInitialized } = useTireWheelPackage();
  const [packageQueryProductCodes, setPackageQueryProductCodes] = useState();
  const [hasRunInitialProductQuery, setHasRunInitialProductQuery] =
    useState(false);
  const [isLoading, setIsLoading] = useState();
  const [data, setData] = useState();
  const [oeProductsData, setOeProductsData] = useState([]);
  const [error, setError] = useState();
  const [info, setInfo] = useState();

  const {
    numberOfPages,
    products,
    recommendationAvailable,
    sorts,
    totalNumberOfResults
  } = productQueryRef.current?.mapResponse(data) || {};

  const {
    isSearchAll,
    pageNum,
    query: searchQuery,
    searchText,
    sort
  } = queryParameters || {};

  const searchOption = useMemo(() => {
    if (
      initialSort === SORT_PARAMS.BEST_AVAILABLE_ASC ||
      initialSort === SORT_PARAMS.BEST_SELLER_ASC
    ) {
      return TireSearchOption.All;
    }
    if (initialSort === SORT_PARAMS.RECOMMENDED || recommendationAvailable) {
      return TireSearchOption.TreadWell;
    }
    return TireSearchOption.All;
  }, [recommendationAvailable, initialSort]);

  const filteredNearByStorePreferencesByDistance = useMemo(() => {
    const nearByStores = myStore?.nearByStores;
    return nearByStores
      ?.filter(storeData => nearbyStorePreferences?.includes(storeData.code))
      .map(storeData => storeData.code);
  }, [nearbyStorePreferences, myStore]);

  const productSetSearchType = useMemo(() => {
    const categoryFilters = q?.match(/accessoryCategory:[A-Za-z]+(:)?/g);

    const isSetSearch =
      ((categoryFilters?.length === 1 &&
        categoryFilters[0]?.includes(
          ACCESSSORY_FILTER_TYPES.WINDSHIELD_WIPERS
        )) ||
        pathname?.includes('wipers-catalog') ||
        pathname?.includes('windshield-wipers')) &&
      selectedFitment;

    const setSearchType =
      SetSearchTypes[searchPosition?.toUpperCase()] || SetSearchTypes.SET;
    return isSetSearch ? setSearchType : undefined;
  }, [pathname, q, searchPosition, selectedFitment]);

  const EMPTY_PACKAGE = 'EMPTY_PACKAGE';

  const storeCode = myStore?.code;

  const sortOptions = getSorts(
    sorts,
    Boolean(featureFlags?.[FEATURE_FLAGS.__FF_BEST_AVAILABLE_SORT__])
  );
  const selectedSortFromData = Object.values(sortOptions).find(
    option => option.selected === true
  );

  const currentSort = selectedSortFromData?.code ?? sort;

  const onSelectSort = useCallback(
    sort => {
      clearLastPlpProduct();
      setSort(sort?.value, selectedFilters);
      setTreadwellProductsDisplayType(
        TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL
      );
    },
    [selectedFilters, setSort]
  );

  const toggleAllSearchResults = useCallback(() => {
    let newSort = SORT_PARAMS.RECOMMENDED;

    if (searchOption === TireSearchOption.TreadWell) {
      newSort = featureFlags?.[FEATURE_FLAGS.__FF_BEST_AVAILABLE_SORT__]
        ? SORT_PARAMS.BEST_AVAILABLE_ASC
        : SORT_PARAMS.BEST_SELLER_ASC;
    }

    updateQueryParams({
      pageNum: 0,
      query: createProductSearchQueryFromFilters([], newSort),
      sort: newSort
    });
    setTreadwellProductsDisplayType(
      TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
    );
    clearLastPlpProduct();
  }, [featureFlags, searchOption, updateQueryParams]);

  const productQueryFn = useMemo(() => {
    if (
      (isPackageSearch && !packageQueryProductCodes) ||
      (isFitmentSearch && isFitmentLoading)
    ) {
      return;
    }

    const newProductQueryFn = (() => {
      if (isAccessorySearch) {
        return getStandardProductSearch;
      }

      if (isPrebuiltPackageSearch) {
        return getPrebuiltPackageSearch;
      }

      if (isPackageSearch) {
        if (packageQueryProductCodes === EMPTY_PACKAGE) {
          if (productType === PRODUCT_TYPES.WHEELS) {
            return isStaggeredSearch
              ? getFitmentStaggeredWheelProductSearch
              : getFitmentWheelProductSearch;
          }

          return isStaggeredSearch
            ? getFitmentStaggeredTireProductSearch
            : getFitmentTireProductSearch;
        }

        if (productType === PRODUCT_TYPES.WHEELS) {
          return isStaggeredSearch
            ? getStaggeredWheelPackageSearch
            : getWheelPackageSearch;
        }

        return isStaggeredSearch
          ? getStaggeredTirePackageSearch
          : getTirePackageSearch;
      }

      if (isSizeSearch && productType === PRODUCT_TYPES.TIRES) {
        return getTireSizeProductSearch;
      }

      if (isSizeSearch && productType === PRODUCT_TYPES.WHEELS) {
        return getWheelSizeProductSearch;
      }

      if (isFitmentSearch && productType === PRODUCT_TYPES.WHEELS) {
        if (isStaggeredSearch) {
          return getFitmentStaggeredWheelProductSearch;
        }

        return getFitmentWheelProductSearch;
      }

      if (isFitmentSearch && productType === PRODUCT_TYPES.TIRES) {
        if (isStaggeredSearch) {
          return getFitmentStaggeredTireProductSearch;
        }

        return getFitmentTireProductSearch;
      }

      if (isSearchAll) {
        return getStandardProductSearch;
      }

      if (isStaggeredSearch) {
        return getStaggeredProductSearch;
      }

      return getStandardProductSearch;
    })();

    return newProductQueryFn;
  }, [
    isAccessorySearch,
    isFitmentLoading,
    isFitmentSearch,
    isPackageSearch,
    isPrebuiltPackageSearch,
    isSearchAll,
    isSizeSearch,
    isStaggeredSearch,
    packageQueryProductCodes,
    productType
  ]);

  const productQuery = useMemo(() => {
    let treadwell = null;
    if (isTreadwellFlow) {
      if (
        isInitialTreadwellFlowRef.current &&
        (productType !== PRODUCT_TYPES.TIRES ||
          (!isFitmentSearch && !isPackageSearch))
      ) {
        disableTreadwellFlow();
        isInitialTreadwellFlowRef.current = false;
      } else {
        treadwell = getTreadwellGQLInput(selectedFitment, treadwellPreferences);
      }
    }
    const vehicleInput = getVehicleInput(selectedFitment);

    if (!productQueryFn) {
      return;
    }

    let frontTireSize = isSizeSearch
      ? tireSizes?.front
      : parseTireSize(vehicleInput?.frontTireSize);
    let rearTireSize = isSizeSearch
      ? tireSizes?.rear
      : parseTireSize(vehicleInput?.rearTireSize);

    let frontWheelSize = isSizeSearch
      ? wheelSizes?.front
      : vehicleInput?.frontWheelRimDiameter;
    let rearWheelSize = isSizeSearch
      ? wheelSizes?.rear
      : vehicleInput?.rearWheelRimDiameter;

    if (isSizeSearch && staggeredSearchType === AXLE_TYPES.FRONT) {
      rearTireSize = null;
      rearWheelSize = null;
    }
    if (isSizeSearch && staggeredSearchType === AXLE_TYPES.REAR) {
      frontTireSize = rearTireSize;
      rearTireSize = null;
      frontWheelSize = rearWheelSize;
      rearWheelSize = null;
    }

    return productQueryFn({
      frontTireProductCode: packageQueryProductCodes?.front,
      frontTireSize,
      frontWheelProductCode: packageQueryProductCodes?.front,
      frontWheelSize,
      initialFacets,
      initialFilters,
      nearByStoreCodes: filteredNearByStorePreferencesByDistance,
      pageNum,
      productSetSearchType: productSetSearchType,
      productType,
      queryType,
      rearTireProductCode: packageQueryProductCodes?.rear,
      rearTireSize,
      rearWheelProductCode: packageQueryProductCodes?.rear,
      rearWheelSize,
      searchOption:
        treadwell && searchOption === TireSearchOption.TreadWell
          ? TireSearchOption.TreadWell
          : TireSearchOption.All,
      searchQuery,
      searchText,
      selectedSort: getNormalizedSortNames(queryParameters?.sort),
      shouldApplyFallback: !isFitmentSearch,
      staggeredSearchType,
      staggeredState: isStaggeredSearch ? 'ON' : 'OFF',
      storeCode,
      treadwell: searchOption === TireSearchOption.TreadWell ? treadwell : null,
      vehicleInput: !isSearchAll ? vehicleInput : undefined
    });
  }, [
    disableTreadwellFlow,
    initialFacets,
    initialFilters,
    isFitmentSearch,
    isPackageSearch,
    isSearchAll,
    isSizeSearch,
    isStaggeredSearch,
    isTreadwellFlow,
    filteredNearByStorePreferencesByDistance,
    packageQueryProductCodes,
    pageNum,
    productQueryFn,
    productSetSearchType,
    productType,
    queryType,
    searchQuery,
    searchText,
    selectedFitment,
    queryParameters,
    staggeredSearchType,
    storeCode,
    tireSizes,
    treadwellPreferences,
    wheelSizes,
    searchOption
  ]);

  const fetchOEProducts = useCallback(async () => {
    if (!productQuery) {
      return;
    }
    const queryVariables =
      queryType === QUERY_TYPES.BRAND_SEARCH ||
      queryType === QUERY_TYPES.CATEGORY_SEARCH
        ? {
            search: {
              ...productQuery.variables.search,
              includeBackorderedDiscontinued: true,
              initialFilters: [],
              query: `:${defaultSort}:oe-boolean:Original Equipment`
            }
          }
        : {
            searchInput: {
              ...productQuery.variables.searchInput,
              includeBackorderedDiscontinued: true,
              pageNumber: 0,
              rawQuery: `:${defaultSort}:oe-boolean:Original Equipment`
            }
          };
    const { data } = await client.query({
      query: productQuery.query,
      variables: {
        ...productQuery.variables,
        ...queryVariables
      }
    });
    const { products } = productQuery?.mapResponse(data) || [];
    setOeProductsData(products);
  }, [client, defaultSort, productQuery, queryType]);

  const fetchProducts = useCallback(async () => {
    // ensure that there is only one fetch in flight for a single query
    if (
      !productQuery ||
      (productQuery.query === productQueryRef.current?.query &&
        isEqual(productQuery.variables, productQueryRef.current?.variables))
    ) {
      return;
    }

    productQueryRef.current = productQuery;
    setIsLoading(true);
    setError();
    setInfo();

    let { data, error } = await client.query({ ...productQuery });
    let info;
    let { filters, products, recommendationAvailable } =
      productQuery?.mapResponse(data) || {};

    hasOEProductInTreadwellRef.current =
      filters
        ?.find(filter => filter?.code === 'oe-boolean')
        ?.values?.find(value => value?.code === 'Original Equipment')
        .selected &&
      Boolean(products) &&
      !recommendationAvailable &&
      productQuery.variables.searchInput?.searchOption ===
        TireSearchOption.TreadWell;

    if (data) {
      if (!products && productQuery.variables?.search?.freeText) {
        const withoutVehicleProductQuery = getStandardProductSearch({
          initialFacets,
          initialFilters,
          pageNum,
          productType,
          queryType,
          searchQuery,
          searchText,
          selectedSort: getNormalizedSortNames(queryParameters?.sort),
          storeCode
        });

        ({ data, error } = await client.query({
          ...withoutVehicleProductQuery
        }));

        /* Retry Treadwell query without the in stock filter if there were no
         * results. Ideally this would be handled by Hybris in the future
         */
      } else if (
        !isArrayWithLength(products) &&
        productQuery.variables.searchInput?.searchOption ===
          TireSearchOption.TreadWell &&
        productQuery.variables.searchInput?.rawQuery?.includes(
          'storeInStock:In Stock'
        )
      ) {
        ({ data, error } = await client.query({
          query: productQuery.query,
          variables: {
            ...productQuery.variables,
            searchInput: {
              ...productQuery.variables.searchInput,
              rawQuery: ':recommended'
            }
          }
        }));

        info = {
          id: PLPInfoMessageIds.noInStockTreadwellProducts
        };

        ({ filters, products } = productQuery?.mapResponse(data) || {});
      } else if (
        productQuery.variables.searchInput?.searchOption ===
          TireSearchOption.TreadWell &&
        !hasOEProductInTreadwellRef?.current &&
        !recommendationAvailable
      ) {
        info = {
          id: PLPInfoMessageIds.noTreadwellProducts
        };
      }

      setFilters(
        facetsToFilters(filters, {
          hasGetItFast: true
        })
      );
      setSelectedFilters(selectedFilters =>
        facetsToSelectedFilters(filters, selectedFilters, searchQuery, {
          hasGetItFast: true
        })
      );
    }

    /*
     * TODO: Rewrite this hook.
     * Switching a store will cause two different queries to run in parallel
     * but only the first data will be used because of another useEffect further
     * down that checks if the search is the same.
     */
    if (
      JSON.stringify(productQuery.variables) ===
      JSON.stringify(productQueryRef.current.variables)
    ) {
      setData(data);
      setError(error);
      setInfo(info);
      setIsLoading(false);
    }
  }, [
    productQuery,
    client,
    setFilters,
    setSelectedFilters,
    initialFacets,
    initialFilters,
    pageNum,
    productType,
    queryType,
    searchQuery,
    searchText,
    queryParameters,
    storeCode
  ]);

  const shouldShowTreadwellTopThreeProducts =
    treadwellProductsDisplayType !==
      TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL &&
    isTreadwellFlow &&
    isArrayWithLength(products) &&
    products?.length > 3;

  const getProductsArr = useCallback(() => {
    if (
      treadwellProductsDisplayType ===
      TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
    ) {
      return products?.slice(0, 3);
    }

    return products;
  }, [products, treadwellProductsDisplayType]);

  const productCodes = products?.reduce((acc, product) => {
    if (
      isCanonicalProduct(product) &&
      Array.isArray(product?.aggregates?.codes)
    ) {
      return acc.concat(product.aggregates.codes);
    } else if (
      isStaggeredSearch &&
      staggeredSearchType === STAGGERED_SEARCH_TYPES.SET
    ) {
      acc.push({ front: product?.code, rear: product?.rearProduct?.code });
    } else {
      acc.push(product.code);
    }
    return acc;
  }, []);

  const { doesFitEnumMap, isLoading: isLoadingDoesFit } = useDoesProductFit({
    fitment: selectedFitment,
    isStaggered:
      isStaggeredSearch && staggeredSearchType === STAGGERED_SEARCH_TYPES.SET,
    productCodes
  });

  const doesFitEnums = Object.values(doesFitEnumMap ?? {});
  let allProductsDoesFitEnum = null;

  if (isPrebuiltPackageSearch) {
    allProductsDoesFitEnum = FIT;
  } else if (doesFitEnums?.some(entry => entry === DONT_FIT)) {
    allProductsDoesFitEnum = DONT_FIT;
  } else if (doesFitEnums?.some(entry => entry === FIT)) {
    allProductsDoesFitEnum = FIT;
  } else if (doesFitEnums?.some(entry => entry === EXCLUDED)) {
    allProductsDoesFitEnum = EXCLUDED;
  }

  useEffect(() => {
    setHasRunInitialProductQuery(false);
  }, []);

  useEffect(() => {
    setPackageQueryProductCodes(() => {
      if (!isPackageSearch || !isPackageInitialized) {
        return null;
      }

      return incompletePackage?.product?.code &&
        getNormalizedProductType(incompletePackage.product) !== productType
        ? {
            front: incompletePackage.product.code,
            rear: incompletePackage.rearProduct?.code
          }
        : EMPTY_PACKAGE;
    });
  }, [isPackageSearch, incompletePackage, isPackageInitialized, productType]);

  useEffect(() => {
    if (
      (isFitmentSearch && isFitmentLoading) ||
      !myStore?.code ||
      !Array.isArray(initialFacets) ||
      !productQuery
    ) {
      setData();
      setError();
      setInfo();
      setIsLoading();
    } else {
      fetchProducts();
    }
  }, [
    productQuery,
    fetchProducts,
    initialFacets,
    isFitmentLoading,
    isFitmentSearch,
    myStore,
    packageQueryProductCodes,
    isPackageSearch,
    hash
  ]);

  useEffect(() => {
    setTreadwellProductsDisplayType(
      shouldShowTreadwellTopThreeProducts
        ? TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
        : treadwellProductsDisplayType
    );
  }, [shouldShowTreadwellTopThreeProducts, treadwellProductsDisplayType]);

  // necessary since the initial render of this hook does not perform a service request
  useEffect(() => {
    if (data || error) {
      setHasRunInitialProductQuery(true);
    }
  }, [data, error]);

  return {
    allProducts: products,
    allProductsDoesFitEnum: isLoadingDoesFit ? null : allProductsDoesFitEnum,
    breadcrumbs,
    category,
    clearSelectedFilters,
    currentSort,
    error,
    fetchOEProducts,
    filters,
    getPagenationUrl,
    info,
    isLoading: !hasRunInitialProductQuery || isLoading,
    metaData,
    numberOfPages:
      treadwellProductsDisplayType ===
      TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
        ? 0
        : numberOfPages,
    oeProductsData,
    onSelectSort,
    pageNum,
    productSetSearchType,
    products: getProductsArr(),
    recommendationAvailable:
      recommendationAvailable || hasOEProductInTreadwellRef?.current,
    searchOption,
    searchText,
    selectFilters,
    selectedFilters,
    setPageNum,
    setSelectedFilters,
    setTreadwellProductsDisplayType,
    sortOptions,
    staggeredSearchType,
    subtitle,
    tireSizes,
    toggleAllSearchResults,
    totalNumberOfResults,
    treadwellProductsDisplayType,
    wheelSizes
  };
}
